import React from 'react';

import {NotebookSectionColor} from 'graphql-client/__generated/graphqlGlobalTypes';
import getEntryPersistentStorage, {EntryStoragePropertyEnums} from 'notebook/notes/EntryPersistentStorage';
import EntrySection from 'notebook/notes/sections/EntrySection';

interface SectionedItems {
  header: JSX.Element | null;
  items: Array<JSX.Element>;
}

function renderSection(
  {header, items}: SectionedItems,
  index: number,
  isOpen: boolean,
  handleSectionToggle: (index: number, isOpen: boolean, puuid: string) => void
): React.ReactElement {
  // use provenanceUUID if it exists, otherwise use index
  const provenanceUuid = header?.props?.day?.provenanceUuid;
  const key = `section-${provenanceUuid || index}`;
  const sectionColor: NotebookSectionColor = header?.props?.day?.sectionColor || NotebookSectionColor.DEFAULT;
  return header ? (
    <EntrySection
      header={header}
      key={key}
      isOpen={isOpen}
      onToggle={(isOpen) => {
        handleSectionToggle(index, isOpen, header.props.day.provenanceUuid);
      }}
      sectionColor={sectionColor}
    >
      {items}
    </EntrySection>
  ) : (
    <div key={key}>{items}</div>
  );
}

export default function renderSections(
  indexSlice: {start: number; end: number},
  renderedItems: Array<React.ReactElement>,
  renderSectionHeader: (index: number, start: number) => React.ReactElement | null,
  handleSectionToggle: (index: number, isOpen: boolean, puuid: string) => void,
  sectionState: {[puuid: string]: {[property: string]: boolean}}
): Array<React.ReactElement> {
  const sectionedItems: Array<SectionedItems> = [{header: null, items: []}];
  renderedItems.forEach((item, index) => {
    const header = renderSectionHeader(index + Math.max(indexSlice.start, 0), indexSlice.start);
    if (header) {
      if (index === 0) {
        sectionedItems[sectionedItems.length - 1].header = header;
      } else {
        sectionedItems.push({header, items: []});
      }
    }
    sectionedItems[sectionedItems.length - 1].items.push(item);
  });
  return sectionedItems
    .filter(({items}) => items.length > 0)
    .map((sectionedItem, index) => {
      const puuid = sectionedItem.header?.props?.day?.provenanceUuid;

      const isOpen =
        puuid &&
        getEntryPersistentStorage().getPropertyValueFromPUUID(
          sectionState,
          puuid,
          EntryStoragePropertyEnums.IS_OPEN
        );

      return renderSection(sectionedItem, index, isOpen, handleSectionToggle);
    });
}
